import {
    useState,
    FC,
    createContext,
    useEffect,
    useContext,
    useRef,
    useMemo,
    RefObject,
    createRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import type { Location } from 'history';

interface RouteContextValue {
    ref: RefObject<Location<unknown>[]>;
    routes: Location<unknown>[];
    visitedRef: RefObject<Location<unknown>[]>;
    visitedRoutes: Location<unknown>[];
}

export const RouteContext = createContext<RouteContextValue>({
    ref: createRef(),
    routes: [],
    visitedRef: createRef(),
    visitedRoutes: [],
});

export const useRoute = (): RouteContextValue => useContext(RouteContext);

export const RouteProvider: FC = ({ children }) => {
    const history = useHistory();

    const routesRefPushOnly = useRef([{ ...history.location }] as Location<unknown>[]);
    const routesRef = useRef([{ ...history.location }] as Location<unknown>[]);

    const [routes, setRoutes] = useState([...routesRef.current]);
    const [visitedRoutes, setVisitedRoutes] = useState([...routesRefPushOnly.current]);

    const value = useMemo(
        () => ({ ref: routesRef, routes, visitedRoutes, visitedRef: routesRefPushOnly }),
        [routes, visitedRoutes],
    );

    useEffect(() => {
        const unregister = history.listen((location, action) => {
            if (action === 'POP') {
                routesRef.current.pop();
            } else if (action === 'PUSH') {
                routesRef.current.push({ ...location });
                routesRefPushOnly.current.push({ ...location });
            } else if (action === 'REPLACE') {
                routesRef.current[routesRef.current.length - 1] = { ...location };

                // Ignore replace calls for push only
                routesRefPushOnly.current.push({ ...location });
            }

            setRoutes([...routesRef.current]);
            setVisitedRoutes([...routesRefPushOnly.current]);
        });

        return () => unregister();
    }, [history]);

    return <RouteContext.Provider value={value}>{children}</RouteContext.Provider>;
};

/**
 * @returns the last route in history, if the previous route a user visited
 * used `history.replace` that previous route will not be returned.
 */
export const useLastRouteInHistory = (): Location<unknown> | null => {
    const { routes } = useRoute();

    if (routes.length <= 1) return null;

    return routes[routes.length - 2] ?? null;
};

/**
 * @returns the last route a user visited, ignoring replace calls.
 */
export const useLastVisitedRoute = (): Location<unknown> | null => {
    const { visitedRoutes } = useRoute();

    if (visitedRoutes.length <= 1) return null;

    return visitedRoutes[visitedRoutes.length - 2] ?? null;
};

export const useCurrentRoute = (): Location<unknown> | null => {
    const { routes } = useRoute();

    if (routes.length === 0) return null;

    return routes[routes.length - 1] ?? null;
};
